import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { ArrowRight } from "lucide-react";

import { AuthLayout } from "@/components/auth/AuthLayout";
import { PasswordInput } from "@/components/PasswordInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, useLogin } from "@/hooks/useAuth";
import { getAuthErrorMessage } from "@/lib/auth";

/**
 * Inicio de sesión contra la API (Sanctum SPA por cookie). Tras autenticar,
 * vuelve al destino original que exigió login (`location.state.from`) o, si no
 * hubo uno, a la primera pantalla: Informes en proceso. Si ya hay sesión,
 * /login no es un destino → redirige.
 */
export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const login = useLogin();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  /* Destino al que el guard quería llevar al usuario antes de exigir login.

     Sin ese destino se entra directo a la cola de informes, y no a una portada
     de accesos: esa pantalla se eliminó porque era una escala entre el login y
     el trabajo. Apunta a `/informes` y no a `/` —que redirige ahí igual— para
     no gastar un salto en el camino más transitado de la app. */
  const from =
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname ??
    "/informes";

  // Guest guard: con sesión activa, /login no tiene sentido → al destino.
  if (!isLoading && isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    login.mutate(
      { email, password },
      { onSuccess: () => navigate(from, { replace: true }) },
    );
  }

  return (
    <AuthLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold tracking-tight">Inicia sesión</h1>
        <p className="text-muted-foreground text-caption mt-2">
          Ingresa a tu cuenta para continuar.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email">Correo electrónico</Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            placeholder="tu.correo@consultek.cl"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Contraseña</Label>
            <Link
              to="/recuperar-contrasena"
              className="text-primary text-xs font-semibold hover:underline"
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </div>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {login.isError && (
          <p role="alert" className="text-destructive text-sm font-medium">
            {getAuthErrorMessage(login.error)}
          </p>
        )}

        <Button
          type="submit"
          size="lg"
          className="w-full gap-2"
          disabled={login.isPending}
        >
          {login.isPending ? (
            <span className="border-primary-foreground h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
          ) : (
            <>
              Iniciar sesión
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </form>
    </AuthLayout>
  );
}
